import { ref, computed, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { toParserFormat, BUTTON_TYPES } from 'dashboard/helper/templateHelper';

function hasFlowButton(template) {
  const components = template.content?.components || [];
  const btns = components.find(c => c.type === 'BUTTONS');
  return btns?.buttons?.some(b => b.type === BUTTON_TYPES.FLOW) ?? false;
}

export function useCampaignTemplates(inboxIdRef, options = {}) {
  const store = useStore();
  const marketingOnly = ref(false);
  const { categoryRef = null } = options;

  const getCampaignTemplatesByInbox = useMapGetter(
    'messageTemplates/getCampaignTemplatesByInbox'
  );
  const uiFlags = useMapGetter('messageTemplates/getUIFlags');
  const isFetching = computed(() => uiFlags.value.isFetching);

  const fetchTemplates = async inboxId => {
    if (!inboxId) return;
    await store.dispatch('messageTemplates/get', {
      inbox_id: inboxId,
      channel_type: 'Channel::Whatsapp',
      status: 'approved',
    });
  };

  watch(
    inboxIdRef,
    newId => {
      if (newId) fetchTemplates(newId);
    },
    { immediate: true }
  );

  const filteredTemplates = computed(() => {
    if (!inboxIdRef.value) return [];
    const category = categoryRef?.value || null;
    return getCampaignTemplatesByInbox.value(inboxIdRef.value, {
      marketingOnly: marketingOnly.value,
      category,
    });
  });

  const templateOptions = computed(() => {
    return filteredTemplates.value.map(template => {
      const friendlyName = template.name
        .replace(/_/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase());

      const categoryTag =
        template.category === 'marketing' ? 'Marketing' : 'Utility';
      const flow = hasFlowButton(template);
      const flowTag = flow ? ' \u26A1' : '';

      return {
        value: template.id,
        label: `${friendlyName} (${template.language || 'en'}) \u00B7 ${categoryTag}${flowTag}`,
        template: toParserFormat(template),
        category: template.category,
        hasFlow: flow,
      };
    });
  });

  const getSelectedTemplate = templateId => {
    if (!templateId) return null;
    return (
      templateOptions.value.find(o => o.value === templateId)?.template ?? null
    );
  };

  const toggleMarketingOnly = () => {
    marketingOnly.value = !marketingOnly.value;
  };

  return {
    templateOptions,
    filteredTemplates,
    marketingOnly,
    isFetching,
    getSelectedTemplate,
    toggleMarketingOnly,
    fetchTemplates,
  };
}
